import React from 'react'
import classNames from 'classnames'
import { Media } from '@/payload-types'
import { Label, LabelProps } from '@/ui/atoms/Label'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { Vendor } from '@/ui/atoms/Vendor'
import { UsedIn } from '@/ui/atoms/UsedIn'

export type TeaserCardProps = {
  title: string
  description?: string
  labels?: Array<{
    title: string
    variant?: LabelProps['variant']
  }>
  company?: {
    name: string
    logo?: Media | string
  }
  usedIn?: Array<{ process: string; wiki: string }>
  isAlsoUsedIn?: boolean
  image?: Media | string | null
  className?: string
  appearance?: 'filled' | 'outlined'
}

export const TeaserCard: React.FC<TeaserCardProps> = ({
  title,
  description,
  labels,
  appearance = 'filled',
  company,
  usedIn,
  isAlsoUsedIn,
  image,
  className: classNameFromProps,
}) => {
  return (
    <div
      className={classNames(
        'rounded-md overflow-hidden transition-all duration-200 relative @container/card group',
        appearance === 'filled'
          ? 'bg-bg-subtle canhover:hover:bg-neutral-95 border-[1px] border-border-subtle'
          : 'bg-white border border-border-subtle canhover:hover:bg-bg-subtle canhover:hover:border-bg-subtle',
        classNameFromProps,
      )}
    >
      <div
        className={'flex flex-col @md/card:flex-row items-start @md/card:items-center @md/card:p-2'}
      >
        {image && (
          <div
            className={`relative h-full aspect-16/9 @md/card:aspect-square overflow-hidden w-full @md/card:w-auto @md/card:rounded-sm ${company ? 'min-w-[180px]' : 'min-w-[120px]'}`}
          >
            {/* Use plain img tag for blurred images (already optimized server-side) */}
            {typeof image === 'string' && image.startsWith('/api/blurred-image') ? (
              <img
                src={image}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[102%]"
              />
            ) : (
              <CMSImage
                resource={image}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[102%]"
                fill
              />
            )}
          </div>
        )}
        <div
          className={`relative flex flex-col justify-center gap-2 pb-4 w-full mt-4 @md/card:mt-0 ${company ? 'px-4 @md/card:py-4 @md/card:ps-6 @md/card:pe-3' : 'px-4 @md/card:py-2'}`}
        >
          <div className="flex flex-col @xl/card:flex-row @xl/card:flex-wrap items-start gap-2 @xl/card:gap-x-3 @xl/card:gap-y-2 mb-0">
            <h4
              className={`w-full @xl/card:flex-1 min-w-0 text-title-S-medium text-text-base mb-0 transition-colors`}
            >
              {title}
            </h4>
            {labels && labels.length > 0 && (
              <div className="inline-flex flex-wrap gap-2">
                {labels.map((label, index) => {
                  return (
                    label &&
                    label.title && (
                      <Label
                        key={index}
                        title={label.title}
                        variant={label.variant || 'neutral'}
                        size="small"
                      />
                    )
                  )
                })}
              </div>
            )}
          </div>
          {company && (company.name || company.logo) && (
            <Vendor name={company.name} logo={company.logo} size="extraMini" type="default" />
          )}
          {description && <p className="text-body-XS-regular text-text-faded">{description}</p>}
          <UsedIn
            items={usedIn ?? []}
            isAlsoUsedIn={isAlsoUsedIn}
            showBorder={appearance === 'filled'}
            compact
          />
        </div>
      </div>
    </div>
  )
}
